import {
  AvatarGroup,
  Chip,
  Flex,
  FlexCenter,
  StatusChip,
} from '@elementary/common-ui/components';
import Avatar from '@elementary/common-ui/components/Avatar';
import { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import EditableChip, {
  EditableChipOption,
} from '@elementary/common-ui/components/Chip/EditableChip';
import { TagsChipList } from '@elementary/common-ui/components/ChipList';
import {
  formatTimeDuration,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as TableUpdateIcon } from '@elementary/common-ui/assets/16x16/table-update.svg';
import { pluralize } from 'humanize-plus';
import { Status } from '../../types';

type Props = {
  owners: string[];
  subscribers: string[];
  tags: string[];
  tableAssetTags?: string[];
  lastModificationTime?: string;
  latestExecutionStatus: Status;
  hideStatus?: boolean;
  isEditable?: boolean;
  ownerOptions?: string[];
  tagOptions?: string[];
  isUpdatingOwners?: boolean;
  onUpdateOwners?: (owners: string[]) => Promise<void>;
  onUpdateTags?: (tags: string[]) => Promise<void>;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  row-gap: 16px;
  column-gap: 16px;
`;

const RowLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray2};
  font-size: 12px;
  padding-top: 4px;
`;

const PropertiesContent = styled(Flex)`
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

const PropertiesText = styled(Typography)`
  font-size: 13px;
`;

const PropertiesEmptyState = styled(PropertiesText)`
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const NoPointerEvents = styled.div`
  pointer-events: none;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const UserPropertyRow = styled(Flex)`
  gap: 6px;
  align-items: center;
  padding-inline: 6px;
`;

const UserLabel = styled(Typography)`
  font-size: 14px;
`;

const LastUpdateWrapper = styled(FlexCenter)`
  gap: 8px;
  padding-inline: 8px;
`;

const TagsRow = styled(Flex)`
  align-items: center;
  min-height: 28px;
  flex-wrap: wrap;
  gap: 6px;
`;

const CompactTagsChipList = styled(TagsChipList)`
  padding: 0;
  min-height: auto;
  align-items: flex-start;
`;

const UsersProperty = ({
  users,
  title,
  withLabel,
}: {
  users: string[];
  title: string;
  withLabel?: boolean;
}) =>
  users.length === 0 ? null : (
    <Tooltip title={`${title}: ${users.join(', ')}`}>
      <UserPropertyRow>
        {withLabel && <UserLabel>{title}</UserLabel>}
        <NoPointerEvents>
          <AvatarGroup items={users} />
        </NoPointerEvents>
      </UserPropertyRow>
    </Tooltip>
  );

const TestOverviewHeaderDetails = ({
  owners,
  subscribers,
  tags,
  tableAssetTags,
  lastModificationTime,
  latestExecutionStatus,
  hideStatus,
  isEditable,
  ownerOptions,
  tagOptions,
  isUpdatingOwners,
  onUpdateOwners,
  onUpdateTags,
}: Props) => {
  const ownerChipOptions: EditableChipOption[] = (ownerOptions ?? []).map(
    (o) => ({
      value: o,
      label: o,
      icon: <Avatar value={{ name: o }} size="small" />,
    }),
  );

  return (
    <Grid>
      <RowLabel>Properties</RowLabel>
      <PropertiesContent>
        {isEditable && onUpdateOwners ? (
          <EditableChip
            multiple
            value={owners}
            options={ownerChipOptions}
            onSave={onUpdateOwners}
            isLoading={isUpdatingOwners}
            showSearch
            renderLabel={(selected) =>
              selected.length > 0 ? (
                <>
                  <UserLabel>
                    {pluralize(selected.length, 'Owner', 'Owners')}
                  </UserLabel>
                  <AvatarGroup items={selected} size="small" />
                </>
              ) : (
                `${pluralize(owners.length, 'Owner', 'Owners')}: None`
              )
            }
          />
        ) : (
          !!owners.length && (
            <UsersProperty
              users={owners}
              title={pluralize(owners.length, 'Owner', 'Owners')}
              withLabel
            />
          )
        )}

        {!!subscribers.length && (
          <UsersProperty
            users={subscribers}
            title={pluralize(subscribers.length, 'Subscriber', 'Subscribers')}
          />
        )}

        {!hideStatus && (
          <StatusChip
            status={(latestExecutionStatus as ChipStatus) || 'error'}
          />
        )}

        {lastModificationTime && (
          <Tooltip
            title={`Last table update: ${localizeDate(
              lastModificationTime,
            ).format('L LT')}`}
          >
            <LastUpdateWrapper>
              <TableUpdateIcon />
              <PropertiesText>
                {`${formatTimeDuration(lastModificationTime)} ago`}
              </PropertiesText>
            </LastUpdateWrapper>
          </Tooltip>
        )}
      </PropertiesContent>

      <RowLabel>Test tags</RowLabel>
      <TagsRow>
        {isEditable && onUpdateTags ? (
          <CompactTagsChipList
            value={tags}
            onChange={onUpdateTags}
            buttonText="Add tag"
            autoCompleteOptions={tagOptions}
          />
        ) : tags.length ? (
          tags.map((tag) => <Chip key={tag} label={tag} />)
        ) : (
          <PropertiesEmptyState>No tags</PropertiesEmptyState>
        )}
      </TagsRow>

      <RowLabel>Asset tags</RowLabel>
      <TagsRow>
        {tableAssetTags?.length ? (
          tableAssetTags.map((tag) => <Chip key={tag} label={tag} />)
        ) : (
          <PropertiesEmptyState>No tags</PropertiesEmptyState>
        )}
      </TagsRow>
    </Grid>
  );
};

export default TestOverviewHeaderDetails;
